import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import dayjs from 'dayjs';
import { showAlert } from '../../../../features/slices/applicationSlice';
import {
    useCreateVerosimilitudMutation,
    usePatchVerosimilitudMutation
} from '../../../../services/visitasApi';
import { useTranslation } from 'react-i18next';
import { esFechaSeguimientoPosteriorARegularizaciones } from '../../utils/fechaSeguimientoVerosimilitud';

const useRGRLVerosimilitudHandeler = (params: {
    mode: 'create' | 'edit';
    idVisita: number;
    idVerosimilitud?: number;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation('visitas');

    const [createVerosimilitud] = useCreateVerosimilitudMutation();
    const [updateVerosimilitud] = usePatchVerosimilitudMutation();

    const [isLoadingResponse, setIsLoadingResponse] = useState(false);

    const mapVerosimilitudToRespuestas = () => {
        const data = JSON.parse(localStorage.getItem('verosimilitud') || '[]');
        const fechaSeguimientoUnica = localStorage.getItem('verosimilitudFechaSeguimiento');

        const toIsoDate = (date?: string | null) => {
            if (!date) return undefined;
            return `${date.split('T')[0]}T00:00:00.000Z`;
        };

        return data.map((item: any) => {
            const selected = item.opciones.find((o: any) => o.selected);

            const respuesta: {
                idPregunta: number;
                idTipoRespuesta: number;
                fechaRegularizacion?: string;
                fechaSeguimiento?: string;
                observacion?: string;
            } = {
                idPregunta: item.idPregunta,
                idTipoRespuesta: selected?.id ?? 3
            };

            if (selected?.id === 2) {
                respuesta.fechaRegularizacion = toIsoDate(item.fechaRegularizacion);
                respuesta.fechaSeguimiento = toIsoDate(fechaSeguimientoUnica);
            }

            if (item.observacion) {
                respuesta.observacion = item.observacion;
            }

            return respuesta;
        });
    };

    const handleSubmitVerosimilitud = async () => {
        setIsLoadingResponse(true);

        const respuestas = mapVerosimilitudToRespuestas();

        try {
            if (params.mode === 'edit' && params.idVerosimilitud) {
                await updateVerosimilitud({
                    idVisita: params.idVisita,
                    idVerosimilitud: params.idVerosimilitud,
                    respuestas
                }).unwrap();
            } else {
                await createVerosimilitud({
                    idVisita: params.idVisita,
                    respuestas
                }).unwrap();
            }

            localStorage.removeItem('verosimilitud');
            localStorage.removeItem('verosimilitudFechaSeguimiento');

            dispatch(
                showAlert({
                    title: t('verosimilitud.alerts.submitSuccessTitle'),
                    icon: 'success',
                    confirmText: t('common.accept'),
                    confirmAction: () => navigate(-1),
                    closeAction: () => navigate(-1)
                })
            );
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: t('verosimilitud.alerts.submitErrorTitle'),
                    message: error?.data?.message || t('verosimilitud.alerts.submitErrorMessage'),
                    icon: 'error',
                    confirmText: t('common.accept')
                })
            );
        } finally {
            setIsLoadingResponse(false);
        }
    };

    const getMotivoFechaSeguimientoInvalida = ():
        'faltanteOPasada' | 'noPosteriorARegularizacion' | null => {
        const data = JSON.parse(localStorage.getItem('verosimilitud') || '[]');
        const hayIncumplidos = data.some((item: any) =>
            item.opciones.some((o: any) => o.id === 2 && o.selected)
        );
        if (!hayIncumplidos) return null;

        const fechaSeguimientoUnica = localStorage.getItem('verosimilitudFechaSeguimiento');
        if (!fechaSeguimientoUnica) return 'faltanteOPasada';

        // RN-09 (tope fecha_fin_contrato - 3 meses) no bloquea el guardado por decision
        // de producto (2026-07-31): si el contrato esta por vencer, alcanza con el
        // mensaje en rojo del campo (VerosimilitudGrid.tsx), no impide guardar.
        // Se trunca a YYYY-MM-DD: en edición la fecha llega del back como datetime UTC.
        if (dayjs(fechaSeguimientoUnica.substring(0, 10)).isBefore(dayjs(), 'day')) {
            return 'faltanteOPasada';
        }

        // SMP-430: debe ser posterior a todas las fechas de regularización.
        if (!esFechaSeguimientoPosteriorARegularizaciones(fechaSeguimientoUnica, data)) {
            return 'noPosteriorARegularizacion';
        }

        return null;
    };

    const requestSubmitVerosimilitud = () => {
        const motivoFechaSeguimientoInvalida = getMotivoFechaSeguimientoInvalida();

        if (motivoFechaSeguimientoInvalida === 'faltanteOPasada') {
            dispatch(
                showAlert({
                    title: t('verosimilitud.alerts.missingFollowUpDateTitle'),
                    message: t('verosimilitud.alerts.missingFollowUpDateMessage'),
                    icon: 'error',
                    confirmText: t('verosimilitud.alerts.missingFollowUpDateConfirmText')
                })
            );
            return;
        }

        if (motivoFechaSeguimientoInvalida === 'noPosteriorARegularizacion') {
            dispatch(
                showAlert({
                    title: t('verosimilitud.alerts.followUpDateNotAfterRegularizationTitle'),
                    message: t('verosimilitud.alerts.followUpDateNotAfterRegularizationMessage'),
                    icon: 'error',
                    confirmText: t('verosimilitud.alerts.missingFollowUpDateConfirmText')
                })
            );
            return;
        }

        dispatch(
            showAlert({
                title: t('verosimilitud.alerts.submitTitle'),
                message: t('verosimilitud.alerts.submitMessage'),
                cancelText: t('verosimilitud.alerts.submitCancelText'),
                confirmText: t('verosimilitud.alerts.submitConfirmText'),
                confirmAction: () => handleSubmitVerosimilitud()
            })
        );
    };

    return {
        handleSubmitVerosimilitud,
        requestSubmitVerosimilitud,
        isLoadingResponse
    };
};

export default useRGRLVerosimilitudHandeler;
